import {
  isProcurementFilesReadOnly,
  isProcurementLockedForEdits,
  ProcurementState,
  requiresWinnerSelection,
} from "@boark/shared/types/procurement";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { H2 } from "../../components/h2";
import { ProcurementComments } from "../../components/procurement-comments";
import { ProcurementGeneralFiles } from "../../components/procurement-general-files";
import { ProcurementOrganizationList } from "../../components/procurement-organization-list";
import { ProcurementStateTimeline } from "../../components/procurement-state-timeline";
import { useGetProcurement } from "../../queries/procurement";
import { formatOrganizationLabel } from "../../services/organization";
import { updateProcurement } from "../../services/procurement";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { recordIdToId } from "../../utils/record-id-to-id";

export function ProcurementView() {
  const { id } = useParams();
  const { locale: t } = useLocale();
  const queryClient = useQueryClient();

  if (!id) {
    throw new Error("Procurement ID is required");
  }

  const { data: procurement } = useGetProcurement(id);
  const [updatingState, setUpdatingState] = useState(false);
  const [selectedWinnerId, setSelectedWinnerId] = useState("");

  const empty = t.procurement.descriptionEmpty;

  useEffect(() => {
    if (procurement?.winner) {
      setSelectedWinnerId(recordIdToId(procurement.winner.id));
    } else {
      setSelectedWinnerId("");
    }
  }, [procurement?.winner?.id]);

  function stateLabel(state: ProcurementState) {
    switch (state) {
      case ProcurementState.OPEN:
        return t.procurement.stateOpen;
      case ProcurementState.IN_REVIEW:
        return t.procurement.stateInReview;
      case ProcurementState.CLOSED:
        return t.procurement.stateClosed;
      case ProcurementState.CANCELED:
        return t.procurement.stateCanceled;
    }
  }

  const isLocked = procurement
    ? isProcurementLockedForEdits(procurement.state)
    : false;

  const isFilesReadOnly = procurement
    ? isProcurementFilesReadOnly(procurement.state)
    : false;

  const showWinnerSelection = procurement
    ? requiresWinnerSelection(
        procurement.state,
        procurement.organizations.length,
      )
    : false;

  async function invalidateProcurement() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["procurement", id] }),
      queryClient.invalidateQueries({ queryKey: ["procurements"] }),
    ]);
  }

  async function changeState(newState: ProcurementState) {
    if (!procurement || newState === procurement.state) {
      return;
    }

    setUpdatingState(true);
    try {
      await updateProcurement(id!, { state: newState });
      await invalidateProcurement();
    } finally {
      setUpdatingState(false);
    }
  }

  async function submitWinner() {
    if (!procurement || selectedWinnerId === "") {
      return;
    }

    setUpdatingState(true);
    try {
      await updateProcurement(id!, {
        winnerId: selectedWinnerId,
        state: ProcurementState.CLOSED,
      });
      await invalidateProcurement();
    } finally {
      setUpdatingState(false);
    }
  }

  function buildCreateContractRoute() {
    if (!procurement) {
      return "";
    }

    const params = new URLSearchParams({
      procurementId: id!,
      title: procurement.title,
    });
    if (procurement.winner) {
      params.set("counterPartyId", procurement.winner.id);
    }
    return `/admin/contract/list/create?${params.toString()}`;
  }

  return (
    <div className="w-full min-h-full overflow-x-auto">
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button route="/admin/procurement/list" severity="secondary">
            {t.procurement.backToList}
          </Button>
          <div className="flex-1" />
          <Button
            disabled={isLocked}
            route={isLocked ? undefined : `/admin/procurement/list/${id}/edit`}
            title={isLocked ? t.procurement.lockedForReview : undefined}
          >
            {t.page.edit}
          </Button>
          <Button
            route={`/admin/procurement/list/${id}/delete`}
            severity="danger"
          >
            {t.action.delete}
          </Button>
        </div>
        {procurement && (
          <ProcurementStateTimeline
            disableNext={showWinnerSelection}
            disabled={updatingState}
            loading={updatingState}
            state={procurement.state}
            stateLabel={stateLabel}
            onStateChange={(newState) => {
              void changeState(newState);
            }}
          />
        )}
      </div>
      {procurement && (
        <div className="flex flex-col gap-2">
          <H1>{procurement.title}</H1>
          <Field label={t.procurement.description}>
            {procurement.description || empty}
          </Field>
          <Field label={t.procurement.tags}>
            {procurement.tags.length > 0 ? procurement.tags.join(", ") : empty}
          </Field>
          {procurement.winner && (
            <Field label={t.procurement.winner}>
              {formatOrganizationLabel(procurement.winner)}
            </Field>
          )}
          {procurement.state === ProcurementState.CLOSED && (
            <div className="flex flex-wrap gap-2 mt-2">
              {procurement.contract ? (
                <Button
                  route={`/admin/contract/list/${recordIdToId(procurement.contract.id)}`}
                  severity="secondary"
                >
                  {t.procurement.viewContract}
                </Button>
              ) : (
                <Button route={buildCreateContractRoute()} severity="secondary">
                  {t.procurement.createContract}
                </Button>
              )}
            </div>
          )}
          <Field label={t.procurement.createdBy}>
            {procurement.createdBy.email}
          </Field>
          <Field label={t.procurement.createdAt}>
            {formatDate(procurement.createdAt)}
          </Field>
          <Field label={t.procurement.updatedAt}>
            {formatDate(procurement.updatedAt)}
          </Field>
          {isLocked && !showWinnerSelection && (
            <p className="text-sm text-amber-700 mt-4">
              {t.procurement.lockedForReview}
            </p>
          )}
          <div className="mt-6">
            <H2 className="mb-2">
              {showWinnerSelection
                ? t.procurement.winnerSelect
                : t.organization.organizations}
            </H2>
            <ProcurementOrganizationList
              organizations={procurement.organizations}
              procurementId={id}
              readOnly={isFilesReadOnly}
              winnerSelection={
                showWinnerSelection
                  ? {
                      selectedWinnerId,
                      onSelectWinnerId: setSelectedWinnerId,
                    }
                  : undefined
              }
            />
            {showWinnerSelection && (
              <div className="mt-3 flex justify-end">
                <Button
                  disabled={selectedWinnerId === "" || updatingState}
                  loading={updatingState}
                  onClick={() => {
                    void submitWinner();
                  }}
                >
                  {t.procurement.winnerSubmit}
                </Button>
              </div>
            )}
          </div>
          <div className="mt-6">
            <H2 className="mb-2">{t.procurement.files}</H2>
            <ProcurementGeneralFiles
              procurementId={id}
              readOnly={isFilesReadOnly}
            />
          </div>
          <div className="mt-6">
            <ProcurementComments procurementId={id} />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <span className="font-bold">{label}:</span>
      <span>{children}</span>
    </div>
  );
}
